import LoadingButton from "@/components/common/form/loading-button";
import { UPDATE_ORGANIZATION } from "@/gql/organization";
import { useMutation } from "@apollo/client";
import {
  IOrganization,
  IUpdateOrganizationMutation,
  IUpdateOrganizationMutationVariables,
} from "@shared/common/types/api-private/generated/graphql";
import { ExternalLink } from "lucide-react";
import React, { Dispatch, SetStateAction, useCallback } from "react";

interface IOnboardingGuideProps {
  currentOrganization: IOrganization;
  onClose: () => void;
  previewStep?: number;
  setStep: Dispatch<SetStateAction<number>>;
}

const OnboardingGuide = ({
  currentOrganization,
  onClose,
  previewStep,
  setStep,
}: IOnboardingGuideProps) => {
  const [updateTeam] = useMutation<
    IUpdateOrganizationMutation,
    IUpdateOrganizationMutationVariables
  >(UPDATE_ORGANIZATION);

  const handleComplete = useCallback(async () => {
    await updateTeam({
      variables: {
        organizationId: currentOrganization.id,
        object: { onboardStep: 100 },
      },
    });

    onClose();
  }, [currentOrganization.id, onClose, updateTeam]);

  return (
    <div className="w-[34rem]">
      <div className="flex flex-col justify-center p-8 mt-1 bg-cb-gray-50">
        <div className="flex gap-4">
          <div className="flex-none">
            <img
              src="/v2-icon/onboard-guide-1.svg"
              className="w-8 h-8"
              alt="무료 체험"
            />
          </div>
          <div>
            <div>1. 지금부터 한 달 무료 체험 기간이 시작돼요.</div>
            <ul className="mt-1 ml-4 text-sm list-disc text-cb-gray-600">
              <li>
                체험 기간 내에도 언제든지 원하는 플랜을 설정하고 결제 정보를
                등록하면 사용이 가능해요.
              </li>
            </ul>
          </div>
        </div>

        <div className="my-8 border-t border-cb-gray-300"></div>

        <div className="flex gap-4">
          <div className="flex-none">
            <img
              src="/v2-icon/onboard-guide-2.svg"
              className="w-8 h-8"
              alt="기능 안내"
            />
          </div>
          <div>
            <div>
              2. 한 달 동안 코드앤버터의 모든 기능을 적극 활용해 보세요.
            </div>
            <ul className="mt-1 ml-4 text-sm list-disc text-cb-gray-600">
              <li>팝업 ・ 배너 | 이벤트 페이지 ・ 이벤트 폼</li>
              <li>AI 허브 ・ 인스타그램 피드 | 통계 ・ 대시보드</li>
            </ul>
          </div>
        </div>
        <div className="my-8 border-t border-cb-gray-300"></div>
        <div className="flex gap-4">
          <div className="flex-none">
            <img
              src="/v2-icon/onboard-guide-3.svg"
              className="w-8 h-8"
              alt="플랜 안내"
            />
          </div>
          <div>
            <div>3. 무료 체험 기간이 끝나면 확정 플랜을 안내해 드려요.</div>
            <ul className="mt-1 ml-4 text-sm list-disc text-cb-gray-600">
              <li>
                무료 체험 기간의 순 방문자 수(MAU)를 바탕으로 무료 체험 종료일에
                확정된 플랜을 안내해 드려요.
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="mt-8 text-center">
        <div>
          내 사이트를 변화시키는 마케팅 터닝포인트, 코드앤버터에 오신 것을
          진심으로 환영합니다.
        </div>
        <div className="text-cb-gray-700">
          여러분의 비즈니스 성공에 코드앤버터가 함께할게요.
        </div>
      </div>
      <div className="flex my-8 center-center">
        <a
          href="https://guide.codenbutter.com/"
          target="_blank"
          className="inline-flex gap-2 px-4 border rounded-md center-center h-9 border-cb-gray-400 hover:border-cb-gray-600 hover:bg-cb-gray-100 text-cb-gray-900 hover:text-cb-gray-900"
          data-gtm-id="onboarding_guide_guide"
        >
          가이드 바로가기
          <ExternalLink className="w-4 h-4" />
        </a>
      </div>
      <div className="mt-8">
        <LoadingButton
          theme="primary"
          block
          onClick={() => handleComplete()}
          data-gtm-id="onboarding_guide_start"
        >
          시작하기
        </LoadingButton>
        {previewStep && (
          <LoadingButton
            theme="text"
            className="mt-1"
            block
            size="small"
            onClick={() => {
              setStep(previewStep);
            }}
            data-gtm-id="onboarding_guide_prev"
          >
            이전 단계로
          </LoadingButton>
        )}
      </div>
    </div>
  );
};

export default React.memo(OnboardingGuide);
